import { css, cx } from "@emotion/css";
import {
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import type React from "react";
import { IconButton } from "src/components/Default/LekUI/Button";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import isMobile from "src/helpers/isMobile";
import { useFontSize } from "src/hooks/useFontSize";
import { useKeyPress } from "src/hooks/useKeyPress";
import CloseIcon from "src/images/Icons/newIcons/close.svg";

interface ModalContentProps {
	children: React.ReactNode;
	className?: string;
}

export const ModalContent = ({ children, className }: ModalContentProps) => {
	return (
		<DialogContent className={cx(className, "modal--content")}>
			{children}
		</DialogContent>
	);
};

interface ModalFooterProps {
	children: React.ReactNode;
	vertical?: boolean;
	className?: string;
}

export const ModalFooter = ({
	children,
	vertical,
	className,
}: ModalFooterProps) => {
	return (
		<div
			className={cx(
				"modal-sticky-footer",
				vertical && "modal-sticky-footer--vertical",
				className,
			)}
		>
			{children}
		</div>
	);
};

export const ModalStickyFooter = ModalFooter;

interface ModalTitleProps {
	Icon?: React.ComponentType<{ className?: string }>;
	title: string;
	headerClassName?: string;
}

export const ModalTitle = ({
	Icon,
	title,
	headerClassName,
}: ModalTitleProps) => {
	return (
		<DialogTitle className="flex items-center justify-center flex-col gap-8 pb-0">
			{Icon && <Icon className="icon-normal" />}
			<span className={cx("size-20 m-0", headerClassName)}>{title}</span>
		</DialogTitle>
	);
};

interface ModalHeaderProps {
	Icon?: React.ComponentType<{ className?: string }>;
	title: string;
	description?: React.ReactNode;
	/** Odsuwa tytul od przycisku X, gdy Modal ma onClose. */
	hasCloseButton?: boolean;
	/** Kolor ikony, np. "icon-error" (domyslnie primary). */
	iconClassName?: string;
}

/** Naglowek wyrownany do lewej: ikona + tytul + opis (nowy wzorzec modali). */
export const ModalHeader = ({
	Icon,
	title,
	description,
	hasCloseButton,
	iconClassName = "icon-primary",
}: ModalHeaderProps) => {
	const { modalTextFontSizeClass, modalTitleFontSizeClass } = useFontSize();

	return (
		<div
			className={cx(
				"flex flex-col gap-12",
				hasCloseButton && "modal-header--with-close",
			)}
		>
			<div className="flex items-center gap-12">
				{Icon && <Icon className={cx("icon-super-big", iconClassName)} />}
				<h2 className={cx(modalTitleFontSizeClass, "m-0")}>{title}</h2>
			</div>
			{description && (
				<p className={cx(modalTextFontSizeClass, "text-dark-grey m-0")}>
					{description}
				</p>
			)}
		</div>
	);
};

interface ModalProps {
	children: React.ReactNode;
	onClose?: (event?: unknown) => void;
	// Breakpoint MUI ("sm" = 600px, "md" = 900px) albo stała szerokość w px.
	maxWidth?: string | number;
	isLoading?: boolean;
	open?: boolean;
	className?: string;
}

export function Modal({
	children,
	onClose,
	maxWidth = "sm",
	isLoading,
	className,
}: ModalProps) {
	useKeyPress(["Escape"], (event: KeyboardEvent) => {
		onClose?.(event);
	});

	if (isMobile()) {
		return (
			<SwipeableContent isOpen onClose={onClose}>
				<ModalContent className={className}>
					{isLoading ? (
						<div className="flex-center-row">
							<CircularProgress className="icon-small text-grey" />
						</div>
					) : (
						children
					)}
				</ModalContent>
			</SwipeableContent>
		);
	}

	return (
		<Dialog
			maxWidth={
				typeof maxWidth === "number"
					? false
					: (maxWidth as "xs" | "sm" | "md" | "lg" | "xl")
			}
			fullWidth
			disableAutoFocus
			open
			onClose={onClose}
			className={cx("modal", className)}
			slotProps={{
				paper:
					typeof maxWidth === "number"
						? {
								className: css`
								max-width: ${maxWidth}px;
							`,
							}
						: undefined,
			}}
		>
			<ModalContent>
				{onClose && (
					<IconButton className="modal--close--button" onClick={onClose}>
						<CloseIcon className="icon-normal" />
					</IconButton>
				)}
				{isLoading ? (
					<div className="flex-center-row">
						<CircularProgress className="icon-small text-grey" />
					</div>
				) : (
					children
				)}
			</ModalContent>
		</Dialog>
	);
}
